// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useCallback, useEffect, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import ConfirmationDialog from "../dialog/ConfirmationDialog.js";
import CustomSnackbar from "../ui/Snackbars.js";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import useHttp from "../../utils/http.js";
import {
  perdiemPaymentNetsuiteStatus,
  travelStatus,
} from "../../utils/const.js";
import {
  postPerdiemPayments,
  getPerdiemPaymentSummary,
  getPerdiemPayments,
  postPushPerdiemPaymentsByJobNumber,
  patchPerDiemPayments,
  getTotalPerdiemsCount,
} from "../../utils/end-points.js";
import { isAccessGranted } from "../../utils/oauth.js";
import { PRIVILEGES } from "../../Config.js";
import { getNetSuiteStatusInfo } from "../../theme/status-colors.js";
import {
  generatePerdiemPaymentNumber,
  formatAmount,
} from "../../utils/formatting.js";
import wso2Theme from "../../theme";
import "../../css/PerDiemPayment.scss";

const headCells = [
  {
    id: "per_diem_number",
    label: "Per Diem Payment Number",
    align: "left",
    disablePadding: false,
  },
  {
    id: "payment_currency",
    label: "Payment Currency",
    align: "left",
    disablePadding: false,
  },
  {
    id: "payment_amount",
    label: "Payment Amount",
    align: "left",
    disablePadding: false,
  },
  {
    id: "converted_currency",
    label: "Converted Currency",
    align: "left",
    disablePadding: false,
  },
  {
    id: "converted_amount",
    label: "Converted Amount",
    align: "left",
    disablePadding: false,
  },
  {
    id: "netsuite_status",
    label: "NS Status",
    align: "left",
    disablePadding: false,
  },
  { id: "action", label: "Action", align: "left", disablePadding: false },
];

function EnhancedTableHead(props) {
  const { order, orderBy } = props;

  return (
    <TableHead>
      <TableRow>
        <TableCell
          className="Table-head-cell-checkbox"
          padding="checkbox"
        ></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            className="Head-label"
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const PerDiemPayment = (props) => {
  const { travel, isFetchingPerdiemPaymentSummary, setIsFetchingPerdiemPaymentSummary,isLoading, setIsLoading } = props;
  const { handleRequest } = useHttp();
  const [payments, setPayments] = useState([]);
  const [perdiemsCount, setPerdiemsCount] = useState(0);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [isSaveEnable, setSaveEnable] = useState(false);
  const [isResetEnable, setResetEnable] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState({});
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [displayRemainingBalance, setDisplayRemainingBalance] = useState(0);
  const [confirmationDialogData, setConfirmationDialogData] = useState({
    open: false,
    title: "",
    content: "",
    acceptLabel: "",
    declineLabel: "",
    acceptAlert: null,
    declineAlert: null,
  });
  const [isPaymentSummaryLoading, setPaymentSummaryLoading] = useState(false);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });

  useEffect(() => {
    getActivePerdiemsCount();
  }, [isFetchingPerdiemPaymentSummary]);

  useEffect(() => {
    if (perdiemsCount > 0) {
      fetchPerdiemPaymentData(false);
    }
    setIsFetchingPerdiemPaymentSummary(false);
  }, [perdiemsCount, isFetchingPerdiemPaymentSummary]);

  const getActivePerdiemsCount = useCallback(async () => {
    setIsLoading(true);
    handleRequest(
      getTotalPerdiemsCount.replace("$jobNumber", travel.jobNumber),
      "GET",
      null,
      (perdiemCount) => {
        if (perdiemCount !== undefined) {
          setPerdiemsCount(perdiemCount);
        } else {
          setSnackbarData({
            message: "Error while fetching total number of per diems",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
        setIsLoading(false);
      },
      (error) => {
        setIsLoading(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  }, []);

  const fetchPerdiemPaymentData = useCallback(async (showSuccessSnack) => {
    setPaymentSummaryLoading(true);
    setEditingRowId(null);
    setResetEnable(false);
    setSaveEnable(false);
    const promises = [];

    promises.push(
      new Promise(function (resolve, reject) {
        handleRequest(
          getPerdiemPaymentSummary.replace("$jobNumber", travel.jobNumber),
          "GET",
          null,
          (data) => {
            if (data) {
              setPaymentSummary(data);
              resolve();
            } else {
              setSnackbarData({
                message:
                  "Error while fetching per diem payment summary information",
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          },
          (error) => {
            setSnackbarData({
              message: error,
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            reject();
          }
        );
      })
    );

    promises.push(
      new Promise(function (resolve, reject) {
        handleRequest(
          getPerdiemPayments.replace("$jobNumber", travel.jobNumber),
          "GET",
          null,
          (perdiemPayments) => {
            if (perdiemPayments) {
              setPayments(perdiemPayments);
              resolve();
            } else {
              setSnackbarData({
                message: "Error while fetching per diem payments",
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          },
          (error) => {
            setSnackbarData({
              message: error,
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            reject();
          }
        );
      })
    );

    Promise.all(promises)
      .then(() => {
        if (showSuccessSnack) {
          setSnackbarData({
            message: "Success",
            open: true,
            severity: "success",
            onClose: onCloseSnackbar,
          });
        }
      })
      .catch((error) => {
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      })
      .finally(() => {
        setPaymentSummaryLoading(false);
      });
  }, []);

  const togglePaymentSelection = (paymentId) => {
    if (!paymentId) {
      return;
    }
    if (selectedPayments.includes(paymentId)) {
      setSelectedPayments(
        selectedPayments.filter((payment) => payment !== paymentId)
      );
    } else {
      setSelectedPayments((prev) => [...prev, paymentId]);
    }
  };

  const isSelectDisabled = (row) => {
    const isTravelInProgressOrExtended =
      travel.status === travelStatus.INPROGRESS ||
      travel.status === travelStatus.EXTENDED;
    const hasPushPerdiemsAccess = isAccessGranted([
      PRIVILEGES.PUSH_PER_DIEM_PAYMENTS_TO_NS,
    ]);

    return (
      !isTravelInProgressOrExtended ||
      !hasPushPerdiemsAccess ||
      row.netsuiteStatus === perdiemPaymentNetsuiteStatus.COMPLETE ||
      row.perDiemNumber === null
    );
  };

  const getPerdiemPaymentNumber = () => {
    const totalCount = payments?.length;
    const perDiemNumber = generatePerdiemPaymentNumber(
      travel.jobNumber,
      totalCount
    );
    return perDiemNumber;
  };

  const validatePerDiemData = () => {
    if (!paymentSummary.perDiems || paymentSummary.perDiems.length === 0) {
      setSnackbarData({
        message: "No per diems available.",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
      return;
    }
    if (paymentSummary.perDiems.length > 1) {
      setSnackbarData({
        message:
          "Multiple currencies detected for per diem. Please recheck per diem information",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
      return;
    }

    validateAndAddPerdiemPayment();
  };

  const validateAndAddPerdiemPayment = () => {
    setSaveEnable(false);
    if (remainingBalance <= 0) {
      setSnackbarData({
        message:
          "No available balance for per diem payments. Please verify the details and try again.",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
      return;
    }
    const newPayments = payments.filter(
      (payment) => payment.isNewPayment === true
    );
    if (newPayments.length > 0) {
      let lastAddedPayment = newPayments[newPayments.length - 1];
      lastAddedPayment = validatePerdiemPaymentInformation(
        lastAddedPayment,
        true
      );
      if (!lastAddedPayment.isError) {
        setPayments([...payments, createNewPerdiemPayment()]);
      } else {
        setSaveEnable(false);
      }
    } else {
      let paymentObj = createNewPerdiemPayment();
      setPayments([...payments, paymentObj]);
      if (!validatePerdiemPaymentInformation(paymentObj).isError) {
        setSaveEnable(true);
      }
    }
  };

  const createNewPerdiemPayment = () => {
    setEditingRowId(payments.length);
    let perDiemNumber = getPerdiemPaymentNumber();
    const paymentObj = {
      perDiemNumber,
      paymentCurrency: paymentSummary.perDiems[0].perDiemCurrency,
      isNewPayment: true,
      isError: false,
      isEdited: false,
      modifiedFields: {},
    };
    setResetEnable(true);
    return paymentObj;
  };

  const validatePerdiemPaymentInformation = (paymentObj, showError) => {
    paymentObj.isError = false;
    let errorMessage = "Please re-check the information. ";

    if (!paymentObj.paymentCurrency || !paymentObj.paymentAmount) {
      let errorCount = 0;
      paymentObj.isError = true;
      if (showError) {
        if (!paymentObj.paymentCurrency) {
          errorMessage = errorMessage.concat("Payment Currency, ");
          errorCount += 1;
        }
        if (!paymentObj.paymentAmount) {
          errorMessage = errorMessage.concat("Payment Amount, ");
          errorCount += 1;
        }
        errorMessage = errorMessage.slice(0, -2);
        if (errorCount > 1) {
          errorMessage = errorMessage.concat(" are mandatory.");
        } else {
          errorMessage = errorMessage.concat(" is mandatory.");
        }
        setSnackbarData({
          message: errorMessage,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      } else {
        setSnackbarData({
          message: "",
          open: false,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    }
    if (
      payments?.length > 0 &&
      remainingBalance - (payments?.[editingRowId]?.paymentAmount || 0) < 0
    ) {
      paymentObj.isError = true;
      if (showError) {
        setSnackbarData({
          message:
            "Insufficient balance! The entered amount is greater than the available remaining balance.",
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    }

    return paymentObj;
  };

  const getEditField = (id, fieldName) => {
    switch (fieldName) {
      case "perDiemNumber":
        return (
          <TextField
            variant="outlined"
            size="small"
            disabled
            value={payments[id][fieldName] ? payments[id][fieldName] : ""}
          />
        );

      case "paymentCurrency":
        return (
          <TextField
            variant="outlined"
            size="small"
            disabled
            value={payments[id][fieldName] ? payments[id][fieldName] : ""}
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
          />
        );

      case "paymentAmount":
        return (
          <TextField
            variant="outlined"
            size="small"
            type="number"
            value={payments[id][fieldName] ? payments[id][fieldName] : ""}
            onChange={(event) =>
              setEditFieldValue(parseFloat(event.target.value), id, fieldName)
            }
          />
        );

      case "convertedCurrency":
        return (
          <TextField
            variant="outlined"
            size="small"
            disabled
            value={payments[id][fieldName] ? payments[id][fieldName] : "-"}
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
          />
        );

      case "convertedAmount":
        return (
          <TextField
            variant="outlined"
            size="small"
            disabled
            value={payments[id][fieldName] ? payments[id][fieldName] : "-"}
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
          />
        );

      case "netSuiteStatus":
        return getNetSuiteStatus(payments[id]);

      default:
        return (
          <TextField
            variant="outlined"
            size="small"
            value={payments[id][fieldName] ? payments[id][fieldName] : ""}
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
          />
        );
    }
  };

  const calculatePerDiemPayments = () => {
    const totalPerDiemAmount = paymentSummary.perDiems?.reduce(
      (total, item) => total + (item.totalPerDiemAmount || 0),
      0
    );
    const totalCurrencyCostTypeInvoiceAmount = paymentSummary.invoice
      ? paymentSummary.invoice.invoiceAmount || 0
      : 0;

    const totalTravelCardAmount = paymentSummary.travelCard
      ? paymentSummary.travelCard.travelCardAmount || 0
      : 0;
    const totalPayments = payments.reduce(
      (total, payment) => total + (payment.paymentAmount || 0),
      0
    );
    const filteredTotalPayments = payments.reduce((total, payment, index) => {
      return index === editingRowId
        ? total
        : total + (payment.paymentAmount || 0);
    }, 0);

    setDisplayRemainingBalance(
      parseFloat(
        (
          totalPerDiemAmount -
          totalCurrencyCostTypeInvoiceAmount -
          totalTravelCardAmount -
          totalPayments
        ).toFixed(2)
      )
    );

    setRemainingBalance(
      parseFloat(
        (
          totalPerDiemAmount -
          totalCurrencyCostTypeInvoiceAmount -
          totalTravelCardAmount -
          filteredTotalPayments
        ).toFixed(2)
      )
    );
  };

  useEffect(() => {
    calculatePerDiemPayments();
  }, [payments, paymentSummary, editingRowId]);

  const setEditFieldValue = (value, id, fieldName) => {
    let paymentObj = payments[id];
    let newValue =
      value === "" || isNaN(value) || value === "-" ? null : Number(value);
    paymentObj[fieldName] = newValue;

    if (
      !paymentObj.isNewPayment &&
      (fieldName === "paymentCurrency" || fieldName === "paymentAmount")
    ) {
      paymentObj.modifiedFields = {
        ...paymentObj.modifiedFields,
        [fieldName]: value,
      };
      paymentObj.isEdited = true;
    }

    paymentObj = validatePerdiemPaymentInformation(paymentObj, true);
    let paymentArr = [...payments];
    paymentArr[id] = paymentObj;
    setPayments(paymentArr);

    const allPaymentsValid = paymentArr.every(
      (payment) => !validatePerdiemPaymentInformation(payment, true).isError
    );
    setSaveEnable(allPaymentsValid);
  };

  const resetPayments = () => {
    setConfirmationDialogData({
      open: true,
      title: "Reset Per Diem Payments",
      content:
        "Are you sure you want reset per diem payments into last saved values?",
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handleAcceptResetAlert,
      declineAlert: handleDeclineAlert,
    });
  };

  const savePayments = () => {
    setConfirmationDialogData({
      open: true,
      title: "Save Per Diem Payments",
      content: "Are you sure you want to save these per diem payments?",
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handleAcceptSaveAlert,
      declineAlert: handleDeclineAlert,
    });
  };

  const handleAcceptResetAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    fetchPerdiemPaymentData(false);
  };

  const handleAcceptSaveAlert = useCallback(() => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    setIsLoading(true);

    const newPayments = payments.filter((payment) => payment.isNewPayment);
    const editedPayments = payments.filter(
      (payment) => payment.isEdited && !payment.isNewPayment
    );

    const promises = [];

    if (newPayments.length > 0) {
      const sanitizedPayments = newPayments.map(
        ({ perDiemNumber, paymentCurrency, paymentAmount }) => ({
          perDiemNumber,
          paymentCurrency,
          paymentAmount,
        })
      );
      promises.push(
        new Promise((resolve, reject) => {
          handleRequest(
            postPerdiemPayments.replace("$jobNumber", travel.jobNumber),
            "POST",
            sanitizedPayments,
            (response) => {
              if (response) {
                resolve();
              } else {
                setSnackbarData({
                  message: "Error while inserting per diem payments.",
                  open: true,
                  severity: "error",
                  onClose: onCloseSnackbar,
                });
                reject();
              }
              setIsLoading(false);
            },
            (error) => {
              setSnackbarData({
                message: error,
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
              setIsLoading(false);
            }
          );
        })
      );
    }

    if (editedPayments.length > 0) {
      const editedPayload = editedPayments.map((payment) => ({
        id: payment.id,
        paymentCurrency:
          payment.modifiedFields?.paymentCurrency || payment.paymentCurrency,
        paymentAmount:
          payment.modifiedFields?.paymentAmount || payment.paymentAmount,
      }));

      promises.push(
        new Promise((resolve, reject) => {
          handleRequest(
            patchPerDiemPayments.replace("$jobNumber", travel.jobNumber),
            "PATCH",
            editedPayload,
            (response) => {
              if (response) {
                resolve();
              } else {
                setSnackbarData({
                  message: "Error while updating per diem payments.",
                  open: true,
                  severity: "error",
                  onClose: onCloseSnackbar,
                });
                reject();
              }
              setIsLoading(false);
            },
            (error) => {
              setSnackbarData({
                message: error,
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
              setIsLoading(false);
            }
          );
        })
      );
    }

    Promise.all(promises)
      .then(() => {
        setPayments((prevPayments) =>
          prevPayments.map((payment) =>
            payment.isEdited
              ? { ...payment, modifiedFields: {}, isEdited: false }
              : payment
          )
        );
        fetchPerdiemPaymentData(true);
      })
      .catch(() => {
        setSnackbarData({
          message: "An error occurred while saving per diem payments.",
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
        setIsLoading(false); 
      });
  }, [payments, travel.jobNumber, fetchPerdiemPaymentData]);

  const handleDeclineAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
  };

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  const getNetSuiteStatus = (payment) => {
    let statusChip = getNetSuiteStatusInfo(payment.netsuiteStatus);
    return (
      <span>
        <span
          className="Status-chip"
          style={{
            backgroundColor: statusChip[1],
            color: statusChip[2],
          }}
        >
          {statusChip[0]}
        </span>
      </span>
    );
  };

  const handlePushPerdiemPaymentsToNS = () => {
    setConfirmationDialogData({
      open: true,
      title: "Push Per Diem Payments To NS",
      content: `Are you sure you want to push the ${selectedPayments.length} selected per diem payment(s) to NetSuite? Please note that this action cannot be undone.`,
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handlePushPerdiems,
      declineAlert: handleDeclineAlert,
    });
  };

  const isEditDisabled = (row) => {
    const isTravelInProgressOrExtended =
      travel.status === travelStatus.INPROGRESS ||
      travel.status === travelStatus.EXTENDED;
    const hasEditPerdiemsAccess = isAccessGranted([
      PRIVILEGES.EDIT_PER_DIEM_PAYMENTS,
    ]);

    return (
      !isTravelInProgressOrExtended ||
      !hasEditPerdiemsAccess ||
      row.netsuiteStatus === perdiemPaymentNetsuiteStatus.COMPLETE
    );
  };

  const editPerDiemPayments = (row, key) => {
    setEditingRowId(key);
    setResetEnable(true);
    setPayments((prevPayments) =>
      prevPayments.map((payment, index) =>
        index === key
          ? {
              ...payment,
              modifiedFields: { ...payment.modifiedFields },
              isEdited: true,
            }
          : payment
      )
    );
  };

  const handlePushPerdiems = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    setIsLoading(true);
    handleRequest(
      postPushPerdiemPaymentsByJobNumber.replace(
        "$jobNumber",
        travel.jobNumber
      ),
      "POST",
      selectedPayments,
      (paymentResult) => {
        if (paymentResult) {
          if (paymentResult.failedPerDiemPaymentIds?.length > 0) {
            setSnackbarData({
              message: `Error while pushing following per diem payments to Net Suite: 
                                ${paymentResult.failedPerDiemPaymentIds.join(
                                  ", "
                                )}`,
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            setSelectedPayments([]);
            fetchPerdiemPaymentData(false);
          } else {
            setSelectedPayments([]);
            fetchPerdiemPaymentData(true);
          }
          setIsLoading(false);
        } else {
          setIsLoading(false);
          setSnackbarData({
            message: "Error while pushing per diem payments to Net Suite",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      },
      (error) => {
        setIsLoading(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  };

  return (
    <div className="Perdiem-payment-information">
      <ConfirmationDialog confirmationDialogData={confirmationDialogData} />
      <CustomSnackbar snackbar={snackbarData} />

      <div className="Summary-section">
        <div className="Summary-title">Payment Summary</div>
        {isPaymentSummaryLoading ? (
            <Grid container spacing={2}>
            {[...Array(5)].map((_, index) => (
              <Grid item xs={12} sm={6} sx={{ flexBasis: { md: "20%" } }} key={index}>
                <Skeleton variant="rectangular" height={75} sx={{ borderRadius: '8px' }} />
              </Grid>
            ))}
          </Grid>          
         ) : perdiemsCount > 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} sx={{ flexBasis: { md: "20%" } }}>
              <Tooltip title="The total per diem amount found for this job number and currency">
                <div className="Summary-item">
                  <span className="Summary-label">Total Per Diem Amount</span>
                  <div className="Summary-value right-aligned">
                    {paymentSummary.perDiems?.length > 0
                      ? paymentSummary.perDiems.map((item, index) => (
                          <div key={`perDiem-${index}`}>
                            {`${
                              item.totalPerDiemAmount !== undefined &&
                              item.totalPerDiemAmount !== null
                                ? item.totalPerDiemAmount.toFixed(2)
                                : "0.00"
                            } (${item.perDiemCurrency ?? "N/A"})`}
                          </div>
                        ))
                      : "-"}
                  </div>
                </div>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ flexBasis: { md: "20%" } }}>
              <Tooltip title="The invoices that are paid as currency">
                <div className="Summary-item">
                  <span className="Summary-label">Initial Currency Paid</span>
                  <div className="Summary-value right-aligned">
                    {paymentSummary.invoice
                      ? `${paymentSummary.invoice.invoiceAmount.toFixed(2)} (${
                          paymentSummary.invoice.currency
                        })`
                      : "-"}
                  </div>
                </div>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ flexBasis: { md: "20%" } }}>
              <Tooltip title="The total travel card amount found for this job number and currency">
                <div className="Summary-item">
                  <span className="Summary-label">
                    Total Travel Card Amount
                  </span>
                  <div className="Summary-value right-aligned">
                    {paymentSummary.travelCard
                      ? `${paymentSummary.travelCard.travelCardAmount.toFixed(
                          2
                        )} (${paymentSummary.travelCard.currency})`
                      : "-"}
                  </div>
                </div>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ flexBasis: { md: "20%" } }}>
              <Tooltip title="The amount added to the payments so far">
                <div className="Summary-item">
                  <span className="Summary-label">Total Payment Amount</span>
                  <div className="Summary-value right-aligned">
                    {`${payments
                      .reduce(
                        (total, payment) =>
                          total + (payment.paymentAmount || 0),
                        0
                      )
                      .toFixed(2)} (${
                      paymentSummary?.perDiems?.length > 0
                        ? paymentSummary.perDiems[0].perDiemCurrency
                        : "N/A"
                    })`}
                  </div>
                </div>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ flexBasis: { md: "20%" } }}>
              <Tooltip title="Remaining Balance is calculated as: Total Per Diem Amount - Initial Currency Paid - Total Travel Card Amount - Total Payment Amount. This represents the outstanding per diem amount available for further payments.">
                <div className="Summary-item">
                  <span className="Summary-label">Remaining Balance</span>
                  <div className="Summary-value-balance right-aligned">
                    {`${
                      displayRemainingBalance !== undefined &&
                      displayRemainingBalance !== null
                        ? displayRemainingBalance.toFixed(2)
                        : "0.00"
                    } (${
                      paymentSummary?.perDiems?.length > 0
                        ? paymentSummary.perDiems[0].perDiemCurrency
                        : "N/A"
                    })`}
                  </div>
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        ) : (
          <div className="No-per-diems-message">
            Per Diem Summary is not applicable as no per diems are available for
            this job number.
          </div>
        )}
      </div>

      {isAccessGranted([PRIVILEGES.PUSH_PER_DIEM_PAYMENTS_TO_NS]) && (
        <Grid container>
          <Grid item lg={12} className="Button-grid-netsuite">
            <Button
              variant="contained"
              size="small"
              color="secondary"
              className="Push-to-netsuite-button"
              startIcon={<CurrencyExchangeIcon />}
              disabled={selectedPayments.length === 0}
              onClick={handlePushPerdiemPaymentsToNS}
            >
              Push Payments to NS
              {selectedPayments.length > 0 && (
                <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                  ({selectedPayments.length})
                </span>
              )}
            </Button>
          </Grid>
        </Grid>
      )}

      <div>
        <TableContainer className="Table-container">
          <Table size={"small"}>
            <EnhancedTableHead />
            <TableBody>
              {payments.map((row, key) => {
                return (
                  <TableRow hover tabIndex={-1} key={key}>
                    <TableCell
                      className="Table-cell Table-cell-checkbox"
                      padding="checkbox"
                    >
                      <Checkbox
                        color="secondary"
                        onChange={() => togglePaymentSelection(row.id)}
                        disabled={isSelectDisabled(row)}
                        checked={
                          isSelectDisabled(row)
                            ? true
                            : selectedPayments.includes(row.id)
                        }
                      />
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-per-diem-number">
                      {key === editingRowId
                        ? getEditField(key, "perDiemNumber")
                        : row.perDiemNumber}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-currency">
                      {key === editingRowId
                        ? getEditField(key, "paymentCurrency")
                        : row.paymentCurrency}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-cost">
                      {key === editingRowId
                        ? getEditField(key, "paymentAmount")
                        : row.paymentAmount
                        ? row.paymentAmount
                        : "-"}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-currency">
                      {key === editingRowId
                        ? getEditField(key, "convertedCurrency")
                        : row.convertedCurrency
                        ? row.convertedCurrency
                        : "-"}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-cost">
                      {key === editingRowId
                        ? getEditField(key, "convertedAmount")
                        : row.convertedAmount
                        ? formatAmount(row.convertedAmount)
                        : "-"}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-net-suite-status">
                      {getNetSuiteStatus(row)}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-action">
                      <EditIcon
                        color="secondary"
                        className="Edit-icon"
                        onClick={() => {
                          if (!isEditDisabled(row)) {
                            editPerDiemPayments(row, key);
                          }
                        }}
                        disabled={isEditDisabled(row)}
                        style={{
                          color: isEditDisabled(row)
                            ? wso2Theme.palette.disabled.main
                            : wso2Theme.palette.secondary.main,
                          cursor: isEditDisabled(row) ? "default" : "pointer",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!isLoading && payments.length === 0 && (
            <div className="No-payments-label">No Per Diem Payments</div>
          )}
        </TableContainer>
        <Grid container className="Button-grid">
          <Grid item lg={3} className="Button-div-add-payment">
            {isAccessGranted([PRIVILEGES.ADD_PER_DIEM_PAYMENTS]) && (
              <Button
                variant="contained"
                size="small"
                color="secondary"
                className="Add-payment-button"
                disabled={
                  perdiemsCount < 1 ||
                  !(
                    travel.status === travelStatus.INPROGRESS ||
                    travel.status === travelStatus.EXTENDED
                  )
                }
                startIcon={<AddCircleOutlineIcon />}
                onClick={validatePerDiemData}
              >
                Add Per Diem Payment
              </Button>
            )}
          </Grid>
          {isAccessGranted([PRIVILEGES.ADD_PER_DIEM_PAYMENTS]) && (
            <Grid item lg={9} className="Button-div-save-reset">
              <Button
                variant="contained"
                size="small"
                color="secondary"
                className="Save-button"
                disabled={!isSaveEnable || payments.length === 0}
                onClick={savePayments}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                className="Reset-button"
                disabled={!isResetEnable}
                onClick={resetPayments}
              >
                Reset
              </Button>
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default PerDiemPayment;
